import { BiIconChat, BiIconCross } from '@bi-digital/icons-react';
import { useMediaQuery } from '@bino/utils/hooks';
import { BiButton } from 'bi-web-components-react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChooseYourPath from '../components/chooseYourPath/chooseYourPath';
import { ContactForm } from '../components/contactForm/contactForm';
import { FrontContent } from '../components/frontContent/frontContent';
import { ComponentSelector } from '../types';
import styles from './contactUs.module.css';

declare global {
    interface Window {
        EmbeddedMessagingReady?: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        embeddedservice_bootstrap?: any;
    }
}

export type ContactUsProps = {
    mainTextHours1: string;
    mainTextHours2: string;
    mainTextChatHours: string;
    portalUrl: string;
    biaxChat?: boolean;
};

export const params =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams('');

const shouldRenderContactUsOpen = Boolean(params.get('contactusopen') === 'true');

export const ContactUs = (props: ContactUsProps) => {
    const { t } = useTranslation('chat');
    const isSmallScreen = useMediaQuery('(max-width: 770px)');
    const closeButtonRef = useRef<HTMLBiButtonElement>(null);

    const [expandContactUs, setExpandContactUs] = useState(shouldRenderContactUsOpen);
    const [showComponent, setShowComponent] = useState(true);
    const [selector, setSelector] = useState<ComponentSelector>('front');

    useEffect(() => {
        const onEmbeddedMessagingWindowClosed = () => {
            setShowComponent(true);
            setExpandContactUs(false);
        };
        const onEmbeddedMessagingWindowMaximized = () => setShowComponent(false);
        const onEmbeddedMessagingConversationOpened = () => setShowComponent(false);
        window.addEventListener('onEmbeddedMessagingWindowClosed', onEmbeddedMessagingWindowClosed);
        window.addEventListener('onEmbeddedMessagingWindowMaximized', onEmbeddedMessagingWindowMaximized);
        window.addEventListener('onEmbeddedMessagingConversationOpened', onEmbeddedMessagingConversationOpened);
    }, []);

    useEffect(() => {
        if (closeButtonRef.current && selector === 'front' && expandContactUs) {
            closeButtonRef.current.focus();
        }
    }, [expandContactUs, selector]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        // Toggle body class when showChat changes
        document.body.classList.toggle('chat-shown', expandContactUs);

        if (expandContactUs) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [expandContactUs]);

    useEffect(() => {
        if (props.biaxChat) {
            setExpandContactUs(true);
            setSelector('front');
        }
    }, [props.biaxChat]);

    function close() {
        setExpandContactUs(false);
        setSelector('none');
    }

    function openContactUs() {
        setExpandContactUs(true);
        setSelector('front');
    }

    function componentSelector(selector: string) {
        if (selector === 'front')
            return (
                <FrontContent
                    setComponent={setSelector}
                    mainTextHours1={props.mainTextHours1}
                    mainTextHours2={props.mainTextHours2}
                    mainTextChatHours={props.mainTextChatHours}
                    biaxChat={props.biaxChat}
                />
            );
        else if (selector === 'contactForm') return <ContactForm setComponent={setSelector} />;
        else if (selector === 'chooseYourPath')
            return <ChooseYourPath setComponent={setSelector} portalURL={props.portalUrl} />;
        else return null;
    }

    return (
        <>
            {showComponent && (
                <>
                    {!expandContactUs && (
                        <div className={classNames(styles.chatHelpButton, 'chatHelpButton')}>
                            <BiButton variant='filled' onClick={() => openContactUs()}>
                                {isSmallScreen ? (
                                    <BiIconChat
                                        color='white'
                                        size='medium'
                                        title={t('headerTitle')}
                                        aria-hidden='false'
                                    />
                                ) : (
                                    <>
                                        <BiIconChat color='white' slot='icon-left' size='medium' aria-hidden='true' />
                                        {t('headerTitle')}
                                    </>
                                )}
                            </BiButton>
                        </div>
                    )}
                    {expandContactUs && (
                        <div className={classNames(styles.contactUs, expandContactUs ? styles.show : styles.hide)}>
                            <div className={styles.contactUsHeaderContent}>
                                <span className={styles.contactUsHeaderTitle}>{t('headerTitle')}</span>
                                {!props.biaxChat && (
                                    <BiButton
                                        // @ts-expect-error Ref exists but the types is wrong
                                        ref={closeButtonRef}
                                        className={styles.contactUsHeaderButton}
                                        onClick={close}
                                        color='white'
                                        size='small'
                                        variant='text'
                                    >
                                        <BiIconCross color='white' size='small' title={t('closeContactSnapIn')} />
                                    </BiButton>
                                )}
                            </div>
                            <div className={styles.contactUsContent}>{componentSelector(selector)}</div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};
